import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import desksCountIcon from '../../../../../assets/images/floor-selector/desks-count-icon-floor-selector.svg';
import roomsCountIcon from '../../../../../assets/images/floor-selector/rooms-count-icon-floor-selector.svg';
import { closeRoomDetailsPopup } from '../room-details-popup';
import { resolveBrandingUrl } from '../../../common/brand-logo/resolve-branding-url';
import { isHiddenOnKiosk } from '../../util/show-on-kiosk';
import { YOU_ARE_HERE } from '../../../../../constants';
import { getContext, getFloorAnalytics, getLocation, getSelectedLocation, } from '../../../redux/spaces-slice';
const fallbackBrandLogo = require('../../../../../assets/images/cisco_brand_logo.svg');
const Card = styled.div `
  position: fixed;
  top: 1vw;
  left: 1.2vw;
  width: clamp(19.5rem, 25.5vw, 26.5rem);
  min-height: clamp(5.3rem, 5.85vw, 6rem);
  padding: clamp(0.75rem, 0.85vw, 0.85rem) clamp(0.9rem, 1vw, 1rem);
  display: flex;
  align-items: center;
  gap: clamp(0.9rem, 1vw, 1rem);
  border-radius: clamp(0.95rem, 1vw, 1rem);
  background: linear-gradient(
    180deg,
    rgba(32, 41, 52, 0.96) 0%,
    rgba(28, 36, 46, 0.96) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(111, 127, 149, 0.22),
    0 16px 32px rgba(0, 0, 0, 0.24);
  z-index: 2;

  @media (max-width: 768px) {
    top: 0.8rem;
    left: 0.8rem;
    width: min(calc(100vw - 1.6rem), 23.5rem);
  }
`;
const LogoWrap = styled.div `
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Fixed box so the card size stays stable regardless of the logo's aspect
     ratio. Bumped ~15% then a further ~10% (orig was
     clamp(4.8rem,5.8vw,5.9rem) x clamp(2.9rem,3.3vw,3.5rem)). */
  width: clamp(6.07rem, 7.34vw, 7.47rem);
  height: clamp(3.67rem, 4.18vw, 4.43rem);
  overflow: hidden;

  img {
    /* Scale the whole logo down to fit inside the box, preserving aspect ratio
       with no cropping (contain, not cover). */
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;
const Divider = styled.div `
  width: 1px;
  align-self: stretch;
  background: rgba(152, 165, 182, 0.28);
`;
const TextBlock = styled.div `
  flex: 1 1 auto;
  min-width: 0;
`;
const BuildingText = styled.div `
  color: rgba(188, 197, 208, 0.8);
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.72rem, 0.82vw, 0.84rem);
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const FloorText = styled.div `
  margin-top: 0.12rem;
  color: #f6f8fb;
  font-family: 'Sora', sans-serif;
  font-size: clamp(1.08rem, 1.45vw, 1.55rem);
  font-weight: 700;
  line-height: 1.05;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ToggleButton = styled.button `
  appearance: none;
  border: 0;
  width: clamp(3rem, 3.2vw, 3.2rem);
  height: clamp(3rem, 3.2vw, 3.2rem);
  border-radius: clamp(0.7rem, 0.76vw, 0.76rem);
  background: linear-gradient(180deg, #59677c 0%, #4b586c 100%);
  box-shadow:
    inset 0 1px 0 rgba(134, 149, 170, 0.28),
    0 6px 16px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex: 0 0 auto;
  position: relative;
`;
const Chevron = styled.span `
  width: 0.72rem;
  height: 0.72rem;
  border-right: 0.18rem solid #f6f8fb;
  border-bottom: 0.18rem solid #f6f8fb;
  transform: translateY(-0.08rem) rotate(45deg);
`;
const MenuWrap = styled.div `
  position: fixed;
  top: calc(1vw + clamp(5.3rem, 5.85vw, 6rem) + 3rem);
  left: calc(
    9vw + clamp(19.5rem, 25.5vw, 26.5rem) - clamp(12.6rem, 13.8vw, 14rem)
  );
  /* Widened (was clamp(14rem, 16vw, 16.5rem)) to recover the row space the larger
     short-name circle takes, so the room + desk availability chips stay on one
     line instead of wrapping. */
  width: clamp(17rem, 19vw, 19.5rem);
  z-index: 3;

  @media (max-width: 768px) {
    top: calc(0.8rem + clamp(5.3rem, 5.85vw, 6rem) + 0.75rem);
    left: calc(0.8rem + min(calc(100vw - 1.6rem), 23.5rem) - 12.8rem);
    right: auto;
    width: min(calc(100vw - 1.6rem), 20.5rem);
  }
`;
const MenuArrow = styled.div `
  position: absolute;
  top: -0.72rem;
  left: clamp(2.7rem, 3vw, 3.1rem);
  width: 1.18rem;
  height: 1.18rem;
  background: linear-gradient(
    180deg,
    rgba(31, 39, 49, 0.98) 0%,
    rgba(27, 34, 43, 0.98) 100%
  );
  transform: rotate(45deg);
  border-top-left-radius: 0.16rem;
  box-shadow: inset 0 1px 0 rgba(111, 127, 149, 0.18);
  z-index: 0;
`;
const Menu = styled.div `
  position: relative;
  max-height: min(50vh, 24rem);
  overflow-y: auto;
  padding: 0.55rem 0.45rem 0.45rem;
  border-radius: 0.95rem;
  background: linear-gradient(
    180deg,
    rgba(31, 39, 49, 0.98) 0%,
    rgba(27, 34, 43, 0.98) 100%
  );
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.32);
  z-index: 1;

  /* Top scroll-fade shadow, as a sticky overlay ABOVE the rows (z-index) so it's
     actually visible over the first item (an inset shadow gets painted over by
     the row backgrounds). It's a black→transparent gradient (a shadow, not a
     recolour) and is kept tall with a single gentle linear fade — the taller the
     ramp, the smaller the per-pixel alpha step, so the fade-out is imperceptible
     and never reads as a boundary line on the row. Overlay-only via the negative
     bottom margin so it consumes no layout space. */
  &::before {
    content: '';
    position: sticky;
    top: -9px;
    display: block;
    height: 3.4rem;
    margin: -0.55rem -0.45rem -3.4rem;
    border-radius: 0.95rem 0.95rem 0 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.55) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    pointer-events: none;
    z-index: 2;
  }
`;
const MenuItem = styled.button `
  appearance: none;
  width: 100%;
  border: 0;
  padding: 0.62rem 0.78rem;
  margin: 0;
  border-radius: 0.72rem;
  background: ${({ $selected }) => $selected
    ? 'linear-gradient(180deg, rgba(102, 138, 189, 0.92) 0%, rgba(77, 111, 161, 0.92) 100%)'
    : 'rgba(79, 92, 109, 0.58)'};
  color: #f6f8fb;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 2.4rem;

  & + & {
    margin-top: 0.6rem;
  }
`;
const MenuItemMain = styled.div `
  display: flex;
  align-items: center;
  gap: 0.64rem;
  min-width: 0;
`;
const ShortNameCircle = styled.div `
  /* Perfect circle (width === height). Short names are clamped to 5 chars at the
     data layer; to keep them on a single line inside the circle, the font shrinks
     for longer strings. Digits and short names (<= 3 chars) keep the original
     0.88rem size — only 4/5-char names scale down so they fit without overflow. */
  width: 3.9rem;
  height: 3.9rem;
  border-radius: 999px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Keep the text off the circle's edges even for the widest 5-char strings. */
  padding: 0 0.28rem;
  box-sizing: border-box;
  overflow: hidden;
  background: ${({ $selected }) => $selected ? 'rgba(255, 255, 255, 0.26)' : 'rgba(104, 119, 139, 0.78)'};
  color: #eef3fb;
  font-family: 'Sora', sans-serif;
  font-size: ${({ $chars }) => ($chars !== null && $chars !== void 0 ? $chars : 0) >= 5 ? '0.62rem' : $chars === 4 ? '0.78rem' : '0.95rem'};
  font-weight: 700;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  white-space: nowrap;
`;
const MenuText = styled.div `
  min-width: 0;
  text-align: left;
`;
const MenuTitleRow = styled.div `
  display: flex;
  align-items: center;
  gap: 0.72rem;
  min-width: 0;
`;
const MenuTitle = styled.div `
  color: #f6f8fb;
  font-family: 'Sora', sans-serif;
  font-size: 0.9rem;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MetaRow = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.3rem;
`;
const MetaBadge = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 0.24rem;
  min-height: 1.06rem;
  padding: 0.14rem 0.34rem;
  border-radius: 0.32rem;
  background: ${({ $selected }) => $selected ? 'rgba(255, 255, 255, 0.14)' : 'rgba(137, 149, 166, 0.28)'};
  color: rgba(236, 242, 250, 0.9);
  font-family: 'Sora', sans-serif;
  font-size: 0.52rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;
const MetaIcon = styled.img `
  width: 0.52rem;
  height: 0.52rem;
  display: block;
  opacity: 0.92;
`;
const CircleWrap = styled.div `
  /* Relative anchor so the "You Are Here" pill can sit at the base of the circle
     without consuming any row layout space. Keeps the circle's own footprint. */
  position: relative;
  flex: 0 0 auto;
  display: flex;
`;
const YouAreHereBadge = styled.div `
  /* Rounded-rectangle "You Are Here" pill anchored to the base of the circle
     (absolutely positioned, so it adds no extra space) — mirrors how the
     building-view floor selector overlays it at the bottom of the circle.
     Overflows horizontally if wider than the circle, centered underneath it. */
  position: absolute;
  bottom: -0.2rem;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.14rem 0.42rem;
  border-radius: 0.44rem;
  border: 0.08rem solid rgba(255, 255, 255, 0.92);
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 0.5rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
`;
const MenuChevron = styled.span `
  width: 0.4rem;
  height: 0.4rem;
  border-right: 0.1rem solid rgba(246, 248, 251, 0.88);
  border-bottom: 0.1rem solid rgba(246, 248, 251, 0.88);
  transform: rotate(-45deg);
  flex: 0 0 auto;
  align-self: center;
  margin-left: 0.75rem;
`;
// The "All" circle shows the localized "All" label, which can be long in some
// languages (e.g. Polish "Wszystkie"). Mirror the floor short-name behaviour of
// capping at 5 characters: when longer, show the first 3 characters + ".." so it
// never overflows the circle.
const formatAllShortName = (label) => label.length > 5 ? `${label.slice(0, 3)}..` : label;
const FloorSelectorCard = ({ building, floor, selectFloor, }) => {
    const { t } = useTranslation();
    const contextInfo = useSelector(getContext);
    const locationData = useSelector(getLocation);
    const selectedLocationId = useSelector(getSelectedLocation);
    const floorAnalytics = useSelector(getFloorAnalytics);
    const [isOpen, setIsOpen] = useState(false);
    const cardRef = useRef(null);
    const menuRef = useRef(null);
    // Ordered list of logo sources to try, most-preferred first:
    //   1. CloudFront proxy (raw upstream URL rewritten to the kiosk origin)
    //   2. Raw upstream URL straight from contextInfo (e.g. S3)
    //   3. Default Cisco logo
    // Falsy/duplicate entries are removed so each candidate is tried once.
    const logoSources = useMemo(() => {
        const proxied = resolveBrandingUrl(contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.brandingUrl);
        const ordered = [proxied, contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.brandingUrl, fallbackBrandLogo];
        return ordered.filter((src, index) => Boolean(src) && ordered.indexOf(src) === index);
    }, [contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.brandingUrl]);
    const handleLogoError = (event) => {
        var _a;
        const img = event.currentTarget;
        const currentIndex = Number((_a = img.dataset.sourceIndex) !== null && _a !== void 0 ? _a : '0');
        const nextIndex = currentIndex + 1;
        // Advance to the next candidate; stop once every source has been tried
        // (prevents an infinite error loop if even the default logo fails).
        if (nextIndex >= logoSources.length) {
            return;
        }
        img.dataset.sourceIndex = String(nextIndex);
        img.src = logoSources[nextIndex];
    };
    const isFloorViewType = (contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.viewType) === 'floor';
    // "You Are Here" gating — identical to the building-view floor selector so the
    // badge only appears on the floor where-am-I resolves to. When
    // `isWhereAmIEnabled` is explicitly set we honor it; otherwise we fall back to
    // whether a device location is known. `deviceLocationId` is the where-am-I
    // floor id (same source the useFloors hook compares against).
    const deviceLocationId = contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.deviceLocationId;
    const shouldShowWhereAmI = 'isWhereAmIEnabled' in contextInfo && contextInfo.isWhereAmIEnabled !== null
        ? contextInfo.isWhereAmIEnabled
        : deviceLocationId !== null && deviceLocationId !== undefined;
    const floorOptions = useMemo(() => {
        var _a;
        const children = [...((_a = locationData === null || locationData === void 0 ? void 0 : locationData.children) !== null && _a !== void 0 ? _a : [])];
        return (children
            // Hide any floor explicitly flagged `showOnKioskAndWebApp === false`.
            .filter((child) => !isHiddenOnKiosk(child))
            .sort((a, b) => a.floorNumber - b.floorNumber)
            .map((child) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const hasNumber = child.floorNumber !== null && child.floorNumber !== undefined;
            // Label shown BESIDE the circle: the floor's short name (falls back to
            // the full name, then L<number>, when no short name is configured).
            const shortName = child.shortName ||
                child.name ||
                (hasNumber ? `L${child.floorNumber}` : '-');
            // Text shown INSIDE the circle: the floor number (falls back to the
            // short name for floors with no number). Clamped to 5 chars so it
            // always fits the circle.
            const circleText = (hasNumber ? String(child.floorNumber) : shortName).slice(0, 5);
            return {
                id: child.id,
                floorNumber: child.floorNumber,
                circleText,
                shortName,
                roomAvailableCount: (_d = (_c = (_b = (_a = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics[child.id]) === null || _a === void 0 ? void 0 : _a.availabilityInfo) === null || _b === void 0 ? void 0 : _b.roomInfo) === null || _c === void 0 ? void 0 : _c.availableCount) !== null && _d !== void 0 ? _d : null,
                deskAvailableCount: (_h = (_g = (_f = (_e = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics[child.id]) === null || _e === void 0 ? void 0 : _e.availabilityInfo) === null || _f === void 0 ? void 0 : _f.deskInfo) === null || _g === void 0 ? void 0 : _g.availableCount) !== null && _h !== void 0 ? _h : 0,
            };
        }));
    }, [floorAnalytics, locationData]);
    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };
    const handleSelect = (floorId) => {
        closeRoomDetailsPopup();
        setIsOpen(false);
        selectFloor(floorId);
    };
    useEffect(() => {
        if (!isOpen)
            return;
        const handlePointerDown = (event) => {
            const target = event.target;
            if ((cardRef.current && cardRef.current.contains(target)) ||
                (menuRef.current && menuRef.current.contains(target))) {
                return;
            }
            setIsOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [isOpen]);
    return (_jsxs(_Fragment, { children: [_jsxs(Card, Object.assign({ ref: cardRef }, { children: [_jsx(LogoWrap, { children: _jsx("img", { src: logoSources[0], "data-source-index": "0", alt: "Brand Logo", onError: handleLogoError }) }), _jsx(Divider, {}), _jsxs(TextBlock, { children: [_jsx(BuildingText, Object.assign({ title: building }, { children: building })), _jsx(FloorText, Object.assign({ title: floor }, { children: floor }))] }), !isFloorViewType && (_jsx(ToggleButton, Object.assign({ id: "floor_selector_button", onClick: handleToggle }, { children: _jsx(Chevron, {}) })))] })), isOpen && !isFloorViewType && (_jsxs(MenuWrap, Object.assign({ ref: menuRef }, { children: [_jsx(MenuArrow, {}), _jsxs(Menu, { children: [!(contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.defaultFloorId) && (_jsxs(MenuItem, Object.assign({ onClick: () => handleSelect('All') }, { children: [_jsxs(MenuItemMain, { children: [_jsx(ShortNameCircle, Object.assign({ "$chars": formatAllShortName(t('floor.room-search-popup.all'))
                                                    .length }, { children: formatAllShortName(t('floor.room-search-popup.all')) })), _jsx(MenuText, { children: _jsx(MenuTitle, Object.assign({ title: t('floor.show-on-map-toggle.all') }, { children: t('floor.show-on-map-toggle.all') })) })] }), _jsx(MenuChevron, {})] }))), floorOptions.map((option) => (_jsxs(MenuItem, Object.assign({ "$selected": selectedLocationId === option.id, onClick: () => handleSelect(option.id) }, { children: [_jsxs(MenuItemMain, { children: [_jsxs(CircleWrap, { children: [_jsx(ShortNameCircle, Object.assign({ "$selected": selectedLocationId === option.id, "$chars": (option.circleText || '').length }, { children: option.circleText })), shouldShowWhereAmI && option.id === deviceLocationId && (_jsx(YouAreHereBadge, { children: t(YOU_ARE_HERE) }))] }), _jsxs(MenuText, { children: [_jsx(MenuTitleRow, { children: _jsx(MenuTitle, Object.assign({ "$selected": selectedLocationId === option.id, title: option.shortName }, { children: option.shortName })) }), (option.roomAvailableCount !== null ||
                                                        option.deskAvailableCount !== null) && (_jsxs(MetaRow, { children: [option.roomAvailableCount !== null && (_jsxs(MetaBadge, Object.assign({ "$selected": selectedLocationId === option.id }, { children: [_jsx(MetaIcon, { src: roomsCountIcon, alt: "Rooms available" }), option.roomAvailableCount, ' ', t('floor.counts-widget.available')] }))), _jsxs(MetaBadge, Object.assign({ "$selected": selectedLocationId === option.id }, { children: [_jsx(MetaIcon, { src: desksCountIcon, alt: "Desks available" }), option.deskAvailableCount, ' ', t('floor.counts-widget.available')] }))] }))] })] }), _jsx(MenuChevron, {})] }), option.id)))] })] })))] }));
};
export default FloorSelectorCard;
